import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, formatError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const showDemo = new URLSearchParams(location.search).get("demo") === "1";

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      const dashDest =
        u.role === "owner" ? "/owner" :
        u.role === "parent" ? "/parent" :
        u.role === "student" ? "/student" :
        (u.role === "cleaner" || u.role === "service_provider") ? "/cleaner" :
        "/admin";
      const dest = location.state?.from?.pathname || dashDest;
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  function fill(email, password) {
    setEmail(email);
    setPassword(password);
  }

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12 grid-bg">
      <Card className="w-full max-w-md border-border/60">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground"><Sparkles size={16} /></div>
            <div className="font-display font-bold text-primary">QuadScrub</div>
          </div>
          <h1 className="font-display text-3xl font-bold text-primary">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your QuadScrub account.</p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="••••••••" />
            </div>
            {err && <div className="text-sm text-destructive">{err}</div>}
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={busy} data-testid="login-submit">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {showDemo && (
          <div className="mt-6 text-xs text-muted-foreground border-t pt-4">
            <div className="font-semibold mb-2">Try a demo account</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => fill("owner@collegeclean.com", "Owner@2026")} className="px-2 py-1 rounded bg-secondary/10 hover:bg-secondary/20 text-secondary font-semibold" data-testid="demo-owner">Owner</button>
              <button type="button" onClick={() => fill("parent@test.com", "Parent@2026")} className="px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid="demo-parent">Parent</button>
              <button type="button" onClick={() => fill("student@test.com", "Student@2026")} className="px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid="demo-student">Student</button>
              <button type="button" onClick={() => fill("cleaner@test.com", "Cleaner@2026")} className="px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid="demo-cleaner">Cleaner</button>
              <button type="button" onClick={() => fill("admin@collegeclean.com", "Admin@2026")} className="px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid="demo-admin">Platform admin</button>
              <button type="button" onClick={() => fill("uadmin.baylor@collegeclean.com", "UAdmin@2026")} className="px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid="demo-uadmin">Baylor admin</button>
              <button type="button" onClick={() => fill("manager.baylor@collegeclean.com", "Manager@2026")} className="px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid="demo-manager">Community mgr</button>
            </div>
          </div>
          )}

          <div className="mt-6 text-sm text-center text-muted-foreground">
            No account yet? <Link to="/register" className="text-secondary font-semibold" data-testid="goto-register">Sign up</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
